import React, { useEffect, useState, useCallback, useMemo, type ReactNode, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, ChevronRight, Building2, User, Mail, Phone, Lock, Shield, FileText, ArrowRight } from "lucide-react";
import logo from "../assets/images/company-logo.png";
import { useIsMobile } from "../components/ui/use-mobile";

function StepIndicator({ step, isMobile = false }: { step: number; isMobile?: boolean }) {
  const items = [
    { label: "阅读注册协议", icon: FileText },
    { label: "填写基本信息", icon: User },
    { label: "注册成功", icon: CheckCircle }
  ];
  
  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0',
        padding: '16px 0',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        {items.map((item, i) => {
          const Icon = item.icon;
          const isActive = i === step;
          const isPast = i < step;
          
          return (
            <React.Fragment key={item.label}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  backgroundColor: isActive ? '#003c8c' : isPast ? '#10b981' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s'
                }}>
                  <Icon style={{ width: '18px', height: '18px', color: isActive || isPast ? 'white' : '#9ca3af' }} />
                </div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#003c8c' : isPast ? '#10b981' : '#9ca3af',
                  textAlign: 'center',
                  maxWidth: '60px'
                }}>
                  {item.label}
                </span>
              </div>
              {i < items.length - 1 && (
                <div style={{
                  width: '20px',
                  height: '3px',
                  backgroundColor: isPast ? '#10b981' : '#e5e7eb',
                  margin: '0 8px',
                  borderRadius: '2px'
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      padding: '24px 0',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      {items.map((item, i) => {
        const Icon = item.icon;
        const isActive = i === step;
        const isPast = i < step;
        
        return (
          <React.Fragment key={item.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: isActive ? '#003c8c' : isPast ? '#10b981' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s'
              }}>
                <Icon style={{ width: '22px', height: '22px', color: isActive || isPast ? 'white' : '#9ca3af' }} />
              </div>
              <span style={{
                fontSize: '15px',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#003c8c' : isPast ? '#10b981' : '#9ca3af'
              }}>
                {item.label}
              </span>
            </div>
            {i < items.length - 1 && (
              <div style={{
                width: '60px',
                height: '3px',
                backgroundColor: isPast ? '#10b981' : '#e5e7eb',
                margin: '0 24px',
                borderRadius: '2px'
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function RegisterPage() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* 顶部横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: isMobile ? '24px 0' : '40px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          
          <h1 style={{ 
            fontSize: isMobile ? '22px' : '28px', 
            fontWeight: 700, 
            color: 'white',
            marginBottom: '8px',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            供应商注册
          </h1>
        </div>
      </div>
      
      {/* 内容区域 */}
      <div style={{ padding: isMobile ? '20px 16px' : '40px 20px', minHeight: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
        
        {/* 步骤指示器 */}
        <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
          <StepIndicator step={step} isMobile={isMobile} />
        </div>
        
        {/* 主卡片 */}
        <div style={{
          flex: 1,
          maxWidth: '900px',
          width: '100%',
          margin: isMobile ? '16px auto 0' : '24px auto 0',
          backgroundColor: 'white',
          borderRadius: isMobile ? '16px' : '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {step === 0 && <Agreement onNext={() => setStep(1)} onCancel={() => { window.scrollTo(0, 0); navigate('/'); }} isMobile={isMobile} />}
          {step === 1 && <BasicForm onSuccess={() => setStep(2)} onCancel={() => { window.scrollTo(0, 0); navigate('/'); }} isMobile={isMobile} />}
          {step === 2 && <Success isMobile={isMobile} />}
        </div>
      </div>
    </div>
  );
}

function Agreement({ onNext, onCancel, isMobile = false }: { onNext: () => void; onCancel: () => void; isMobile?: boolean }) {
  const [left, setLeft] = useState(5);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  
  const agreementContent = `根据《中华人民共和国民法典》《中华人民共和国著作权法》《全国人大常委会关于维护互联网安全的决定》《中华人民共和国政府采购法》《中华人民共和国招标投标法》《互联网信息服务管理办法》等有关法律、法规、规章，制订本条款。

一、用户资料
用户同意在申请使用本招标采购平台提供的服务时，向本招标采购平台提供真实、准确、完整的单位或个人资料。如用户的单位或个人资料有任何变更，用户应及时更新注册资料。

二、服务条款的修改
本招标采购平台有权根据需要修改服务条款，本招标采购平台服务条款一旦发生修改或其它变动，将在条款相关页面予以提示。

三、隐私保护
本招标采购平台承诺不会公开、转让或透露其用户的不可公开的注册资料及用户在本招标采购平台中保存的非公开内容。

四、用户的帐号、密码和安全性
用户在本招标采购平台注册成功后，将获得一个帐号和密码。该帐号和密码由用户负责保管，用户可随时改变其密码。

五、收费
本平台保留收取"服务"费用的权利。您因进行投标或获取相关信息等向本平台运营方获取有偿服务或接触本平台服务器而发生的所有应纳税赋均由您自行承担。

六、免责条款
因本招标采购平台无法预见客观环境的改变及任何技术上的问题或其他困难，由此导致的数据损失或其他服务中断等情形，会员用户对此应表示理解。

七、信息的储存及限制
本招标采购平台不对用户所发布信息的删除或储存失败负责。

八、用户信息管理
用户自行承担其所传输内容的所有责任，对本招标采购平台的使用符合法律、法规及规章的要求。

九、保障
用户同意保障和维护本招标采购平台及其他用户的利益。

十、终止服务
用户可根据实际情况，主动终止本招标采购平台提供的服务。

十一、通知与送达
所有发给用户的通知都可通过相关页面的公告、系统通知或手机短信中的一种或多种方式传送。

十二、知识产权
本招标采购平台商标权、运作权、解释权、著作权、所有权、版权等知识产权归本平台唯一所有。

十三、串通投标限制
为维护招标采购活动的公平竞争秩序，防范串通投标行为，用户承诺在参与本平台组织的招标采购活动中，严格遵守以下规定：
1. 不得与其他用户在同一项目报名中使用相同的项目负责人、技术人员等关键人员信息、相同的联系电话或相同的电子邮箱地址；
2. 不得与其他用户在不同项目报名中重复使用相同的项目负责人、技术人员等关键人员信息、相同的联系电话或相同的电子邮箱地址。
本平台有权对用户提交的报名信息进行核查，发现存在上述情形的，将视为存在串通投标嫌疑，有权拒绝相关用户的报名申请、认定其投标无效；已中标的，取消其中标资格。
给本平台或其他相关方造成损失的，相关用户应承担连带赔偿责任；情节严重构成犯罪的，本平台将移交司法机关依法追究刑事责任。

十四、条款的法律解释
以上条款解释权归本平台所有。`;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 标题 */}
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px 24px', borderBottom: '1px solid #f3f4f6' }}>
        <h2 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>注册协议</h2>
        <p style={{ fontSize: isMobile ? '13px' : '14px', color: '#6b7280' }}>请仔细阅读以下服务条款</p>
      </div>
      
      {/* 协议内容 */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '24px 40px' }}>
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          fontSize: isMobile ? '13px' : '14px',
          lineHeight: 1.8,
          color: '#4b5563',
          whiteSpace: 'pre-wrap'
        }}>
          {agreementContent}
        </div>
      </div>
      
      {/* 按钮 */}
      <div style={{ padding: isMobile ? '16px' : '24px 40px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', gap: '16px' }}>
        <button
          disabled={left > 0}
          onClick={onNext}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: isMobile ? '12px 24px' : '14px 32px',
            backgroundColor: left > 0 ? '#e5e7eb' : '#003c8c',
            color: left > 0 ? '#9ca3af' : 'white',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: left > 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {left > 0 ? `同意并下一步 (${left}s)` : '同意并下一步'}
          <ArrowRight style={{ width: '18px', height: '18px' }} />
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: isMobile ? '12px 24px' : '14px 32px',
            backgroundColor: 'white',
            color: '#003c8c',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: '2px solid #003c8c',
            borderRadius: '12px',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          取消
        </button>
      </div>
    </div>
  );
}

type FormState = {
  company_name: string;
  account: string;
  password: string;
  confirm_password: string;
  username: string;
  email: string;
  phone: string;
  captchaId: string;
  captchaCode: string;
  smsCode: string;
};

// FormField 组件必须定义在外部，否则每次渲染会创建新组件导致 input 失焦
const FormField = ({ id, label, icon: Icon, error, children }: { id: string; label: React.ReactNode; icon: any; error?: string; children: React.ReactNode }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
      <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>{label}</label>
      {error && <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>}
    </div>
    {children}
  </div>
);

function BasicForm({ onSuccess, onCancel, isMobile = false }: { onSuccess: () => void; onCancel: () => void; isMobile?: boolean }) {
  const [fs, setFs] = useState<FormState>({
    company_name: "",
    account: "",
    password: "",
    confirm_password: "",
    username: "",
    email: "",
    phone: "",
    captchaId: "",
    captchaCode: "",
    smsCode: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pwVisible, setPwVisible] = useState(false);
  const [cpVisible, setCpVisible] = useState(false);
  const [captchaSvg, setCaptchaSvg] = useState<string>("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsLeft, setSmsLeft] = useState(0);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealData, setAppealData] = useState<{ account: string; company_name: string } | null>(null);
  const [showPhoneExistsModal, setShowPhoneExistsModal] = useState(false);

  useEffect(() => { refreshCaptcha(); }, []);

  function refreshCaptcha() {
    fetch('/api/captcha')
      .then(r => r.json())
      .then(d => {
        if (d && d.id && d.svg) {
          setFs(s => ({ ...s, captchaId: d.id, captchaCode: '' }));
          setCaptchaSvg(d.svg);
        }
      })
      .catch(err => console.error('获取验证码错误:', err));
  }

  function validateField(fieldName: keyof FormState, value: string): string {
    switch (fieldName) {
      case 'company_name':
        return !value.trim() ? "企业名称不能为空" : "";
      case 'account':
        return !/^[A-Z0-9]{18}$/i.test(value.trim()) ? "统一社会信用代码需18位" : "";
      case 'password':
        return !/^[A-Za-z0-9\W_]{8,20}$/.test(value) ? "密码需为8-20位" : "";
      case 'confirm_password':
        return value !== fs.password ? "两次密码不一致" : "";
      case 'username':
        return !value.trim() ? "联系人姓名不能为空" : "";
      case 'email':
        return !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value) ? "邮箱格式不正确" : "";
      case 'phone':
        return !/^\d{11}$/.test(value) ? "手机号需为11位数字" : "";
      case 'smsCode':
        return !value.trim() ? "请输入短信验证码" : "";
      default:
        return "";
    }
  }

  function validateAll(): boolean {
    const e: Record<string, string> = {};
    const fields: (keyof FormState)[] = ['company_name', 'account', 'password', 'confirm_password', 'username', 'email', 'phone', 'smsCode'];
    fields.forEach(f => { e[f] = validateField(f, fs[f]); });
    setErrors(e);
    return Object.values(e).every(v => !v);
  }

  async function getSmsCode() {
    if (smsLeft > 0 || smsSending) return;
    if (!/^\d{11}$/.test(fs.phone)) {
      setErrors(prev => ({ ...prev, phone: "手机号需为11位数字" }));
      return;
    }
    if (!fs.captchaCode) {
      setErrors(prev => ({ ...prev, captchaCode: "请输入图形验证码" }));
      return;
    }

    const captchaVerify = await fetch('/api/captcha/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: fs.captchaId, code: fs.captchaCode })
    }).then(r => r.json()).catch(() => ({ ok: false }));

    if (!captchaVerify?.ok) {
      setErrors(prev => ({ ...prev, captchaCode: "图形验证码错误" }));
      refreshCaptcha();
      return;
    }

    setSmsSending(true);
    const r = await fetch('/api/sms/get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: fs.phone, user_name: fs.account, check_type: 'supplier' })
    }).then(r => r.json()).catch(() => ({ ok: false }));
    setSmsSending(false);
    
    if (r?.ok) {
      setSmsLeft(60);
      setErrors(prev => ({ ...prev, captchaCode: '' }));
    } else if (r?.error === 'phone_exists') {
      // 手机号已被注册，显示弹窗提示
      setShowPhoneExistsModal(true);
    } else {
      setErrors(prev => ({ ...prev, smsCode: r?.error || '短信发送失败' }));
    }
  }

  useEffect(() => {
    if (smsLeft <= 0) return;
    const t = setInterval(() => setSmsLeft(s => s > 0 ? s - 1 : 0), 1000);
    return () => clearInterval(t);
  }, [smsLeft]);

  async function submit() {
    if (!validateAll()) return;
    
    setLoading(true);
    try {
      const dup = await fetch('/api/register/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name: fs.company_name, account: fs.account, email: fs.email, phone: fs.phone })
      }).then(r => r.json());
      
      const d = dup?.duplicates || {};
      
      // 检查账号重复，如果重复则弹出申诉弹窗
      if (d.account) {
        setAppealData({
          account: fs.account,
          company_name: d.existing_company_name || fs.company_name // 如果API返回了现有企业名称则使用，否则使用用户输入的（兜底）
        });
        setShowAppealModal(true);
        setLoading(false);
        return;
      }

      const e: Record<string, string> = {};
      if (d.company_name) e.company_name = "企业名称已存在";
      // if (d.account) e.account = "登录账号已存在"; // 已在上方处理
      if (d.email) e.email = "邮箱地址已存在";
      if (d.phone) e.phone = "联系人手机号已存在";
      
      if (Object.keys(e).some(k => e[k])) {
        setErrors(e);
        setLoading(false);
        return;
      }

      const resp = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: fs.company_name,
          account: fs.account,
          password: fs.password,
          username: fs.username,
          email: fs.email,
          phone: fs.phone,
          sms_code: fs.smsCode,
        })
      }).then(r => r.json());

      if (resp?.ok) {
        onSuccess();
      } else {
        setErrors(prev => ({ ...prev, smsCode: resp?.error || "注册失败" }));
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, smsCode: "注册失败，请重试" }));
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? '#003c8c' : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 标题 */}
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px 24px', borderBottom: '1px solid #f3f4f6' }}>
        <h2 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>填写基本信息</h2>
        <p style={{ fontSize: isMobile ? '13px' : '14px', color: '#6b7280' }}>请填写以下信息完成注册</p>
      </div>
      
      {/* 表单 */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '24px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
          {/* 企业名称 */}
          <FormField id="company_name" label="企业名称 *" icon={Building2} error={errors.company_name}>
            <div style={{ position: 'relative' }}>
              <Building2 style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'company_name' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.company_name}
                onChange={(e) => setFs(s => ({ ...s, company_name: e.target.value }))}
                onFocus={() => setFocusedField('company_name')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('company_name', !!errors.company_name)}
                placeholder="请输入企业名称"
              />
            </div>
          </FormField>

          {/* 登录账号 */}
          <FormField id="account" label="登录账号（统一社会信用代码） *" icon={FileText} error={errors.account}>
            <div style={{ position: 'relative' }}>
              <FileText style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'account' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.account}
                onChange={(e) => setFs(s => ({ ...s, account: e.target.value }))}
                onFocus={() => setFocusedField('account')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('account', !!errors.account)}
                placeholder="18位统一社会信用代码（请手动输入）"
              />
            </div>
          </FormField>

          {/* 密码 */}
          <FormField id="password" label="密码（8-20位） *" icon={Lock} error={errors.password}>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'password' ? '#003c8c' : '#9ca3af' }} />
              <input
                type={pwVisible ? 'text' : 'password'}
                value={fs.password}
                onChange={(e) => setFs(s => ({ ...s, password: e.target.value }))}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                style={{ ...inputStyle('password', !!errors.password), paddingRight: '48px' }}
                placeholder="8-20位数字、字母或特殊字符"
              />
              <button type="button" onClick={() => setPwVisible(!pwVisible)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {pwVisible ? <Eye style={{ width: '20px', height: '20px', color: '#9ca3af' }} /> : <EyeOff style={{ width: '20px', height: '20px', color: '#9ca3af' }} />}
              </button>
            </div>
          </FormField>

          {/* 确认密码 */}
          <FormField id="confirm_password" label="确认密码 *" icon={Lock} error={errors.confirm_password}>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'confirm_password' ? '#003c8c' : '#9ca3af' }} />
              <input
                type={cpVisible ? 'text' : 'password'}
                value={fs.confirm_password}
                onChange={(e) => setFs(s => ({ ...s, confirm_password: e.target.value }))}
                onFocus={() => setFocusedField('confirm_password')}
                onBlur={() => setFocusedField(null)}
                style={{ ...inputStyle('confirm_password', !!errors.confirm_password), paddingRight: '48px' }}
                placeholder="请再次输入密码"
              />
              <button type="button" onClick={() => setCpVisible(!cpVisible)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {cpVisible ? <Eye style={{ width: '20px', height: '20px', color: '#9ca3af' }} /> : <EyeOff style={{ width: '20px', height: '20px', color: '#9ca3af' }} />}
              </button>
            </div>
          </FormField>

          {/* 联系人 */}
          <FormField id="username" label="联系人姓名 *" icon={User} error={errors.username}>
            <div style={{ position: 'relative' }}>
              <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'username' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.username}
                onChange={(e) => setFs(s => ({ ...s, username: e.target.value }))}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('username', !!errors.username)}
                placeholder="请输入联系人姓名"
              />
            </div>
          </FormField>

          {/* 邮箱 */}
          <FormField id="email" label="电子邮箱 *" icon={Mail} error={errors.email}>
            <div style={{ position: 'relative' }}>
              <Mail style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'email' ? '#003c8c' : '#9ca3af' }} />
              <input
                type="email"
                value={fs.email}
                onChange={(e) => setFs(s => ({ ...s, email: e.target.value }))}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('email', !!errors.email)}
                placeholder="请输入邮箱地址"
              />
            </div>
          </FormField>

          {/* 手机号 */}
          <FormField id="phone" label="联系人手机号 *" icon={Phone} error={errors.phone}>
            <div style={{ position: 'relative' }}>
              <Phone style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'phone' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.phone}
                onChange={(e) => setFs(s => ({ ...s, phone: e.target.value }))}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('phone', !!errors.phone)}
                placeholder="请输入手机号"
              />
            </div>
          </FormField>

          {/* 图形验证码 */}
          <FormField id="captchaCode" label="图形验证码 *" icon={Shield} error={errors.captchaCode}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'captchaCode' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.captchaCode}
                  onChange={(e) => setFs(s => ({ ...s, captchaCode: e.target.value }))}
                  onFocus={() => setFocusedField('captchaCode')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('captchaCode', !!errors.captchaCode)}
                  placeholder="请输入验证码"
                />
              </div>
              <div
                onClick={refreshCaptcha}
                style={{
                  width: '120px',
                  height: '48px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb'
                }}
              >
                {captchaSvg ? <div dangerouslySetInnerHTML={{ __html: captchaSvg }} /> : <span style={{ fontSize: '12px', color: '#9ca3af' }}>点击刷新</span>}
              </div>
            </div>
          </FormField>

          {/* 短信验证码 */}
          <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
            <FormField 
              id="smsCode" 
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>短信验证码 *</span>
                  {smsLeft > 0 && <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: 500 }}>验证码有效期60秒，请尽快填写！</span>}
                </div>
              }
              icon={Lock} 
              error={errors.smsCode}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'smsCode' ? '#003c8c' : '#9ca3af' }} />
                  <input
                    value={fs.smsCode}
                    onChange={(e) => setFs(s => ({ ...s, smsCode: e.target.value }))}
                    onFocus={() => setFocusedField('smsCode')}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle('smsCode', !!errors.smsCode)}
                    placeholder="请输入短信验证码"
                  />
                </div>
                <button
                  onClick={getSmsCode}
                  disabled={!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode}
                  style={{
                    padding: '0 24px',
                    height: '48px',
                    backgroundColor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#e5e7eb' : '#003c8c',
                    color: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {smsLeft > 0 ? `重新获取(${smsLeft}s)` : smsSending ? '发送中...' : '获取验证码'}
                </button>
              </div>
            </FormField>
          </div>
        </div>
      </div>
      
      {/* 按钮 */}
      <div style={{ padding: isMobile ? '16px' : '24px 40px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', gap: '16px' }}>
        <button
          onClick={submit}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: isMobile ? '12px 24px' : '14px 48px',
            backgroundColor: loading ? '#9ca3af' : '#003c8c',
            color: 'white',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 4px 15px rgba(0,60,140,0.3)',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {loading ? '注册中...' : '立即注册'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: isMobile ? '12px 24px' : '14px 32px',
            backgroundColor: 'white',
            color: '#003c8c',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: '2px solid #003c8c',
            borderRadius: '12px',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          取消
        </button>
      </div>

      {showAppealModal && appealData && (
        <AppealModal
          visible={showAppealModal}
          data={appealData}
          onCancel={() => setShowAppealModal(false)}
          onSuccess={() => {
            setShowAppealModal(false);
            // 可以添加一个提示toast，这里简单alert
            alert('申诉提交成功，我们将尽快与您联系！');
          }}
        />
      )}

      {/* 手机号已存在弹窗 */}
      {showPhoneExistsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '450px',
            maxWidth: '90%',
            padding: '32px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Phone style={{ width: '30px', height: '30px', color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>提示</h3>
              <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: 1.6 }}>
                该手机号已被注册使用，有任何疑问请联系管理员，感谢您的支持，谢谢！
              </p>
            </div>
            <button
              onClick={() => setShowPhoneExistsModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#003c8c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppealModal({ visible, data, onCancel, onSuccess }: { visible: boolean; data: { account: string; company_name: string }; onCancel: () => void; onSuccess: () => void }) {
  const navigate = useNavigate();

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onCancel}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '500px',
        maxWidth: '90%',
        padding: '32px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ 
            width: '60px', 
            height: '60px', 
            backgroundColor: '#fee2e2', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Shield style={{ width: '30px', height: '30px', color: '#ef4444' }} />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>该账号已被注册</h3>
          <p style={{ marginTop: '8px', color: '#6b7280' }}>
            统一社会信用代码：{data.account}
          </p>
          <p style={{ marginTop: '4px', color: '#6b7280' }}>
            已注册企业名称：{data.company_name}
          </p>
          <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '14px', lineHeight: 1.6 }}>
            如果存在冒名顶替或者忘记账号的情况，可点击下方按钮申请该账号权限。
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={() => {
              window.scrollTo(0, 0);
              navigate('/downloads');
            }}
            style={{
              padding: '12px',
              backgroundColor: 'white',
              color: '#003c8c',
              border: '1px solid #003c8c',
              borderRadius: '8px',
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            跳转指引手册
          </button>
        </div>
      </div>
    </div>
  );
}

function Success({ isMobile = false }: { isMobile?: boolean }) {
  const navigate = useNavigate();
  
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '40px 20px' : '60px 40px' }}>
      <div style={{
        width: isMobile ? '80px' : '120px',
        height: isMobile ? '80px' : '120px',
        borderRadius: '50%',
        backgroundColor: '#ecfdf5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        <CheckCircle style={{ width: isMobile ? '40px' : '60px', height: isMobile ? '40px' : '60px', color: '#10b981' }} />
      </div>
      
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>注册成功！</h2>
      <p style={{ fontSize: isMobile ? '14px' : '16px', color: '#6b7280', marginBottom: isMobile ? '32px' : '48px', textAlign: 'center' }}>
        您的账号已创建成功，请登录后完善企业信息
      </p>
      
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', width: isMobile ? '100%' : 'auto' }}>
        <button
          onClick={() => navigate('/login')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: isMobile ? '12px 24px' : '14px 48px',
            backgroundColor: '#003c8c',
            color: 'white',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,60,140,0.3)',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          立即登录
          <ArrowRight style={{ width: '18px', height: '18px' }} />
        </button>
        <button
          onClick={() => { window.scrollTo(0, 0); navigate('/'); }}
          style={{
            padding: isMobile ? '12px 24px' : '14px 32px',
            backgroundColor: 'white',
            color: '#003c8c',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 600,
            border: '2px solid #003c8c',
            borderRadius: '12px',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          返回首页
        </button>
      </div>
    </div>
  );
}